import React from 'react';
import './styles/breadcrumbs.scss';
import LangA, { TranslateMenuItem } from '../../i18n/LangA';
import { t } from '@i18n/i18n';
import { useI18nInitNamespace } from '@i18n/useI18nInitNamespace';
import sitemapTranslations from '@i18n/translations/pages/sitemap.html.json';
import { Helmet } from 'react-helmet';
import CloudflareImage from '../cloudflare-image/CloudflareImage';
import cx from 'classnames';
import useIsMobile from '../../hooks/useIsMobile';

const BuildSchemaObject = (fullPathNamesTranslated, fullPathUrls, currentPage) => {
  const languagePaths = ['es'];

  const baseUrlArr = window.location.href.split('//');
  const languageExtension = languagePaths.includes(baseUrlArr[1].split('/')[1]) ? '/' + baseUrlArr[1].split('/')[1] : '';

  const baseUrl = baseUrlArr[0] + '//' + baseUrlArr[1].split('/')[0] + languageExtension;

  let counter = 2;

  let schemaObject = `
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [{
        "@type": "ListItem",
        "position": 1,
        "name": "${TranslateMenuItem('Home')}",
        "item": "${baseUrl}"
      }`;

  fullPathNamesTranslated.forEach((element, index) => {
    if (index < fullPathNamesTranslated.length - 1) {
      schemaObject += `
      ,{
        "@type": "ListItem",
        "position": ${counter++},
        "name": "${element}",
        "item": "${`${baseUrl}${fullPathUrls[index]}`}"
      }
      `;
    }
  });

  schemaObject += `
  ,{
    "@type": "ListItem",
    "position": ${counter++},
    "name": "${currentPage}"
  }
  `;

  schemaObject += ']}';

  return schemaObject;
};

const Breadcrumbs = (props) => {
  useI18nInitNamespace('sitemap', sitemapTranslations, true);

  // These paths should be ignored in the Breadcrumbs. Usually the language paths and the paths that do not exist
  const ignorePaths = ['es', !props.showBreadcrumbs && 'services', 'faqs', 'email-verification'];
  const isMobile = useIsMobile();

  let pathName = '';

  if (typeof window !== 'undefined' && window.document) {
    pathName = document.location.pathname;
  }

  const fullPathNames = [];
  const fullPathNamesTranslated = [];
  const fullPathUrls = [];

  let pathBuilder = '/';

  const normalizeName = (name) => {
    const nameWithoutExtension = name.split('.')[0];
    const nameArray = [];

    nameWithoutExtension.split('-').forEach((nameElement) => {
      nameArray.push(nameElement.charAt(0).toUpperCase() + nameElement.slice(1));
    });

    pathBuilder += `${name}/`;
    fullPathUrls.push(pathBuilder);
    return nameArray.join(' ');
  };

  // for each path element take the translation key
  pathName.split('/').forEach((pathElement) => {
    if (pathElement && !ignorePaths.includes(pathElement)) {
      const normalizedName = normalizeName(pathElement);
      let pathItem = Object.keys(sitemapTranslations.en).find(
        (key) => sitemapTranslations.en[key].toLowerCase() === normalizedName.toLowerCase()
      );
      if (pathItem) {
        fullPathNames.push(pathItem);
        fullPathNamesTranslated.push(t(pathItem));
      } else {
        // Handle the VS pages where .com may be missing from the url
        pathItem = Object.keys(sitemapTranslations.en).find(
          (key) => sitemapTranslations.en[key].toLowerCase() === normalizedName.toLowerCase() + '.com'
        );
        if (pathItem) {
          fullPathNames.push(pathItem);
          fullPathNamesTranslated.push(t(pathItem));
        } else {
          fullPathNames.push(pathElement.split('.')[0].replaceAll('-', '_'));
          fullPathNamesTranslated.push(t(pathElement.split('.')[0].replaceAll('-', '_')));
        }
      }
    }
  });

  const currentPage = fullPathNames[fullPathNames.length - 1];

  return (
    fullPathNames &&
    fullPathNames.length > 0 && (
      <React.Fragment>
        <div
          className={
            props.sectionBreadcrumbs
              ? 'website__breadcrumbs sectionBreadcrumbs'
              : props.sitemapBreadcrumbs
              ? 'website__breadcrumbs sitemapBreadcrumbs'
              : 'website__breadcrumbs'
          }
        >
          <ul className={cx('breadcrumbsList', props.additionalClass, !fullPathNames.includes('docs') ? 'website-breadcrumbs' : '')}>
            {(!isMobile || fullPathNames.includes('docs')) && (
              <li>
                <a href={pathName.includes('/es') ? '/es/' : '/'} className="breadcrumbs-home-a" title={t('Home')}>
                  <CloudflareImage src={`/home-icon2x.webp`} alt={t('Home_alt', true)} className="breadcrumbs-home-icon" />
                </a>
              </li>
            )}

            {!fullPathNames.includes('docs') &&
              fullPathNames.map((pathEl, index) => {
                if (index < fullPathNames.length - 1) {
                  return (
                    <React.Fragment key={'breadcrumb-' + index}>
                      {!isMobile && (
                        <li>
                          <CloudflareImage src={`/chevron-right.webp`} className="breadcrumbs-chevron" />
                        </li>
                      )}
                      {isMobile && (
                        <li>
                          <span className={props.mobileColorClass}>&#8592;</span>
                        </li>
                      )}

                      <li key={`breadcrumb${index}`}>
                        <LangA
                          className={props.mobileColorClass}
                          href={pathName.includes('/es/') ? '/es' + fullPathUrls[index] : fullPathUrls[index]}
                          onClick={(e) => pathName.includes('solutions') && e.preventDefault()}
                        >
                          {t(pathEl)}
                        </LangA>
                      </li>
                    </React.Fragment>
                  );
                } else if (1 === fullPathNames.length && isMobile) {
                  return (
                    <React.Fragment key={'docs-breadcrumb-' + index}>
                      <li>
                        <span className={props.mobileColorClass}>&#8592;</span>
                      </li>
                      <li key={`breadcrumb${index}`}>
                        <LangA className={props.mobileColorClass} href={pathName.includes('/es/') ? '/es/' : '/'}>
                          {t('Home')}
                        </LangA>
                      </li>
                    </React.Fragment>
                  );
                } else {
                  // Add a default return statement in case none of the conditions are met
                  return null;
                }
              })}
            {fullPathNames.includes('docs') &&
              fullPathNames.map((pathEl, index) => {
                if (index < fullPathNames.length - 1) {
                  return (
                    <React.Fragment key={'docs-' + index}>
                      <li>
                        <CloudflareImage src={`/chevron-right.webp`} className="breadcrumbs-chevron" />
                      </li>

                      <li key={`breadcrumb${index}`}>
                        <LangA href={pathName.includes('/es/') ? '/es' + fullPathUrls[index] : fullPathUrls[index]}>{t(pathEl)}</LangA>
                      </li>
                    </React.Fragment>
                  );
                } else {
                  // Add a default return statement in case none of the conditions are met
                  return null;
                }
              })}

            {(!isMobile || fullPathNames.includes('docs')) && (
              <React.Fragment>
                <li>
                  <CloudflareImage src={`/chevron-right.webp`} className="breadcrumbs-chevron" />
                </li>
                <li>
                  <span>{t(currentPage)}</span>
                </li>
              </React.Fragment>
            )}
          </ul>
        </div>
        <Helmet>
          <script type="application/ld+json" async>
            {BuildSchemaObject(fullPathNamesTranslated, fullPathUrls, t(currentPage))}
          </script>
        </Helmet>
      </React.Fragment>
    )
  );
};

export default Breadcrumbs;
